import { StyleSheet, ViewProps } from 'react-native'
import { BreadcrumbData, Color, Text } from 'zens-storybook'
import { useCompany, useLocation, useBuilding, useFloorplan } from 'zens-store'
import { useTranslation } from 'react-i18next'
import { useBreadcrumb } from '@dms/hook'

import { LayoutHeader } from '../layout'

import { FloorplanEditorStep } from './editor'

const styles = StyleSheet.create({
  container: {},
  content: {
    justifyContent: 'flex-start',
  },
  subtitle: {
    color: Color.calmGrey,
  },
})

interface Props extends ViewProps {
  companyId: number
  locationId: number
  buildingId: number
  floorId?: number
  breadcrumbItems?: BreadcrumbData[]
  step: FloorplanEditorStep
}

const FloorplanHeader = (props: Props) => {
  const { companyId, locationId, buildingId, floorId = 0, breadcrumbItems, step, style, ...otherProps } = props
  const { t } = useTranslation()
  const company = useCompany({ companyId })
  const location = useLocation({ companyId, locationId })
  const building = useBuilding({ companyId, buildingId })
  const floorplan = useFloorplan({ companyId, floorId })

  const breadcrumb = useBreadcrumb([
    {
      title: company.data?.name || '',
      href: `/companies/${companyId}`,
    },
    {
      title: location.data?.name || '',
      href: `/companies/${companyId}/locations/${locationId}`,
    },
    {
      title: building.data?.name || '',
      href: `/companies/${companyId}/locations/${locationId}/buildings/${buildingId}`,
    },
    ...(floorId
      ? [
          {
            title: floorplan.data?.name || `${t('Floor')} ${floorplan.data?.number}`,
            href: `/companies/${companyId}/locations/${locationId}/buildings/${buildingId}/floors/${floorId}`,
          },
          step === FloorplanEditorStep.Edit
            ? { title: t('Edit') }
            : step === FloorplanEditorStep.Settings
              ? { title: t('Settings') }
              : { title: t('Add') },
        ]
      : []),
    ...(breadcrumbItems || []),
  ])

  function editorSubtitle() {
    switch (step) {
      case FloorplanEditorStep.Add:
      case FloorplanEditorStep.Edit: {
        if (!floorplan.data?.background) return t('Upload floorplan')
        if (!floorplan.data?.scale?.distance || !floorplan.data?.scale?.start || !floorplan.data?.scale?.end)
          return t('Scale floorplan')
        return t('Add workspaces')
      }
      case FloorplanEditorStep.Settings:
        return t('Settings')
    }
  }

  return (
    <LayoutHeader
      breadcrumb={breadcrumb}
      title={`${t('Floorplan editor')} - ${
        floorplan.data?.name || floorplan.data?.number ? `${t('Floor')} ${floorplan.data?.number}` : t('New floor')
      }`}
      accessory={
        <Text level={1} style={styles.subtitle}>
          {editorSubtitle()}
        </Text>
      }
      style={[styles.container, style]}
      contentContainerStyle={styles.content}
      {...otherProps}
    />
  )
}

export default FloorplanHeader
